<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.aiPrompts.createTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <!-- Name -->
        <div class="form-group">
          <label>{{ $t('bo.aiPrompts.labelName') }} <span class="required">*</span></label>
          <input v-model="form.name" type="text" class="input" :placeholder="$t('bo.aiPrompts.namePlaceholder')" />
        </div>

        <div class="form-row">
          <!-- Assistant -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelAssistant') }} <span class="required">*</span></label>
            <select v-model="form.assistant" class="input">
              <option value="sparkie_bot">Sparkie Bot</option>
            </select>
          </div>

          <!-- Use case -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelUseCase') }} <span class="required">*</span></label>
            <select v-model="form.use_case" class="input">
              <option value="goal_suggestion">{{ $t('bo.aiPrompts.useCaseGoalSuggestion') }}</option>
              <option value="bio_generation">{{ $t('bo.aiPrompts.useCaseBioGeneration') }}</option>
              <option value="matching">{{ $t('bo.aiPrompts.useCaseMatching') }}</option>
              <option value="session_preparation">{{ $t('bo.aiPrompts.useCaseSessionPrep') }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <!-- Provider -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelProvider') }} <span class="required">*</span></label>
            <select v-model="form.ai_provider" class="input">
              <option value="mistral">Mistral</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          <!-- Model -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelModel') }} <span class="required">*</span></label>
            <input v-model="form.model" type="text" class="input" :placeholder="$t('bo.aiPrompts.modelPlaceholder')" />
          </div>
        </div>

        <div class="form-row">
          <!-- Language (hidden for bio_generation: always auto / same as input) -->
          <div v-if="form.use_case !== 'bio_generation'" class="form-group">
            <label>{{ $t('bo.aiPrompts.labelLanguage') }} <span class="required">*</span></label>
            <select v-model="form.output_language" class="input">
              <option value="auto">{{ $t('bo.aiPrompts.langAuto') }}</option>
              <option value="fr">Français (fr)</option>
              <option value="en">English (en)</option>
            </select>
          </div>
          <div v-else class="form-group">
            <label>{{ $t('bo.aiPrompts.labelLanguage') }}</label>
            <div class="input input-readonly">{{ $t('bo.aiPrompts.langAuto') }}</div>
            <div class="hint-line">{{ $t('bo.aiPrompts.langAutoBioHint') }}</div>
          </div>

          <!-- Output format -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelOutputFormat') }} <span class="required">*</span></label>
            <select v-model="form.expected_output_format" class="input">
              <option value="plain_text">Plain text</option>
              <option value="json">JSON</option>
              <option value="bullet_list">Bullet list</option>
              <option value="numbered_list">Numbered list</option>
              <option value="markdown">Markdown</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <!-- Temperature -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelTemperature') }}
              <span class="hint">{{ form.temperature }}</span>
            </label>
            <input v-model.number="form.temperature" type="range" min="0" max="1" step="0.05" class="range-input" />
          </div>

          <!-- Max tokens -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelMaxTokens') }}</label>
            <input v-model.number="form.max_tokens" type="number" class="input input-sm" min="100" max="8000" step="100" />
          </div>
        </div>

        <!-- Execution mode -->
        <div class="form-group">
          <label>{{ $t('bo.aiPrompts.labelExecutionMode') }}</label>
          <select v-model="form.execution_mode" class="input input-sm">
            <option value="single">Single</option>
            <option value="chain_step">Chain step</option>
          </select>
        </div>

        <!-- System prompt -->
        <div class="form-group">
          <label>{{ $t('bo.aiPrompts.labelSystemPrompt') }} <span class="required">*</span></label>
          <div class="hint-line">{{ $t('bo.aiPrompts.variableHint') }}</div>
          <textarea v-model="form.system_prompt" class="textarea textarea-tall" rows="5" :placeholder="$t('bo.aiPrompts.systemPromptPlaceholder')" />
        </div>

        <!-- User prompt template -->
        <div class="form-group">
          <label>{{ $t('bo.aiPrompts.labelUserPrompt') }} <span class="required">*</span></label>
          <textarea v-model="form.user_prompt_template" class="textarea textarea-tall" rows="5" :placeholder="$t('bo.aiPrompts.userPromptPlaceholder')" />
        </div>

        <!-- Variables -->
        <div class="form-group">
          <label>{{ $t('bo.aiPrompts.labelVariables') }}</label>
          <div class="tag-input-area">
            <div class="tag-list">
              <span v-for="(v, i) in form.variables" :key="i" class="tag-item">
                {{ '{' + '{' + v + '}' + '}' }}
                <button @click="removeVariable(i)" class="tag-remove">&times;</button>
              </span>
            </div>
            <input
              v-model="variableInput"
              @keydown.enter.prevent="addVariable"
              type="text"
              class="input"
              :placeholder="$t('bo.aiPrompts.variablePlaceholder')"
            />
          </div>
        </div>

        <!-- Garde-fou placeholders / variables (non bloquant) -->
        <div
          v-if="undeclaredPlaceholders.length || unusedVariables.length || isBioUseCase"
          class="prompt-lint"
        >
          <div v-if="undeclaredPlaceholders.length" class="lint-warn">
            ⚠ {{ $t('bo.aiPrompts.warnUndeclaredPlaceholders', { list: undeclaredPlaceholders.join(', ') }) }}
          </div>
          <div v-if="unusedVariables.length" class="lint-info">
            {{ $t('bo.aiPrompts.warnUnusedVariables', { list: unusedVariables.join(', ') }) }}
          </div>
          <div v-if="isBioUseCase" class="lint-info">
            {{ $t('bo.aiPrompts.hintBioLanguage') }}
          </div>
        </div>

        <!-- Internal notes -->
        <div class="form-group">
          <label>{{ $t('bo.aiPrompts.labelNotes') }}</label>
          <textarea v-model="form.internal_notes" class="textarea" rows="2" :placeholder="$t('bo.aiPrompts.notesPlaceholder')" />
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
        <button @click="submit" :disabled="loading || !isValid" class="btn-submit">
          {{ loading ? $t('bo.common.creating') : $t('bo.aiPrompts.createBtn') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import aiPromptService from '@/services/aiPromptService'

const { t } = useI18n()
const emit = defineEmits(['close', 'created'])

const loading = ref(false)
const error = ref('')
const variableInput = ref('')

const form = ref({
  name: '',
  assistant: 'sparkie_bot',
  use_case: 'goal_suggestion',
  execution_mode: 'single',
  ai_provider: 'mistral',
  model: 'mistral-small-latest',
  output_language: 'fr',
  temperature: 0.7,
  max_tokens: 800,
  system_prompt: '',
  user_prompt_template: '',
  variables: [],
  expected_output_format: 'plain_text',
  internal_notes: '',
})

watch(
  () => form.value.use_case,
  (useCase) => {
    if (useCase === 'bio_generation') {
      form.value.output_language = 'auto'
    }
  }
)

const isValid = computed(() =>
  form.value.name.trim() &&
  form.value.system_prompt.trim() &&
  form.value.user_prompt_template.trim() &&
  form.value.model.trim()
)

// --- Garde-fou : cohérence placeholders <-> variables (non bloquant) ---
const extractPlaceholders = (text) => {
  const found = new Set()
  const re = /\{\{\s*([\w.]+)\s*\}\}/g
  let m
  while ((m = re.exec(text || '')) !== null) found.add(m[1])
  return [...found]
}
const usedPlaceholders = computed(() => [
  ...new Set([
    ...extractPlaceholders(form.value.system_prompt),
    ...extractPlaceholders(form.value.user_prompt_template),
  ]),
])
const undeclaredPlaceholders = computed(() =>
  usedPlaceholders.value.filter((p) => !form.value.variables.includes(p))
)
const unusedVariables = computed(() =>
  form.value.variables.filter((v) => !usedPlaceholders.value.includes(v))
)
const isBioUseCase = computed(() => form.value.use_case === 'bio_generation')

const addVariable = () => {
  const val = variableInput.value.trim().replace(/[{}]/g, '')
  if (val && !form.value.variables.includes(val)) {
    form.value.variables.push(val)
    variableInput.value = ''
  }
}

const removeVariable = (i) => form.value.variables.splice(i, 1)

const submit = async () => {
  if (!isValid.value) return
  loading.value = true
  error.value = ''
  try {
    const payload = { ...form.value }
    if (payload.use_case === 'bio_generation') {
      payload.output_language = 'auto'
    }
    if (!payload.internal_notes) delete payload.internal_notes
    if (!payload.variables.length) delete payload.variables
    await aiPromptService.create(payload)
    emit('created')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.aiPrompts.failedCreate')
    if (e.response?.data?.errors) {
      const errs = Object.values(e.response.data.errors).flat()
      error.value = errs.join(' ')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.required { color: #dc2626; }

.hint {
  font-weight: 400;
  color: #6b7280;
  font-size: 0.8rem;
}

.hint-line {
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.prompt-lint {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
}
.prompt-lint .lint-warn {
  font-size: 0.8rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.375rem;
  padding: 0.4rem 0.6rem;
}
.prompt-lint .lint-info {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.4rem 0.6rem;
}

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.input-sm { max-width: 160px; }

.input-readonly {
  background: #f9fafb;
  color: #374151;
  display: flex;
  align-items: center;
  min-height: 2.25rem;
}

.range-input {
  width: 100%;
  cursor: pointer;
  accent-color: #3b82f6;
}

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.textarea-tall {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.6;
}

.tag-input-area {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-height: 1rem;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.6rem;
  background: #f0fdf4;
  color: #15803d;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-family: monospace;
}

.tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #15803d;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
